import PropTypes from "prop-types";

/**
 * RealisticTransformer – High-fidelity 3D power transformer component.
 * Features:
 * - Upward convective thermal heat flow arrows on left & right radiator banks
 * - High-voltage ceramic bushing lightning / corona arcs from H1, H2, H3
 * - Continuous core magnetic induction flux loops
 * - Top conservator tank oil level sight column
 * - Base status indicator LED
 */
const RealisticTransformer = ({
  liveValue,
  isRunning,
  isOffline,
  statusColor,
  speedScale = 1,
  opacity = 1,
  width = "100%",
  height = "100%",
}) => {
  const dotColor = statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";

  const effectiveSpeed = Math.max(0.3, speedScale || 1);
  const heatDuration = isRunning && !isOffline ? `${Math.max(0.4, 1.6 / effectiveSpeed)}s` : "0s";
  const fluxDuration = isRunning && !isOffline ? `${Math.max(0.35, 1.2 / effectiveSpeed)}s` : "0s";

  return (
    <div
      className={`scada-realistic-container ${isOffline ? "scada-realistic--offline" : ""}`}
      style={{
        width,
        height,
        opacity,
      }}
    >
      {/* 3D Realistic Base Image */}
      <img
        src="/scada-realistic/Transformer.png"
        alt="SCADA Realistic Transformer"
        className="scada-realistic-img"
      />

      {/* Dynamic Animated Overlay (SVG synchronized in 490x408 space) */}
      <svg
        className="scada-realistic-overlay"
        viewBox="0 0 490 408"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <radialGradient id="trfArcGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#fde047" stopOpacity="0.95" />
            <stop offset="50%" stopColor="#f59e0b" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#d97706" stopOpacity="0" />
          </radialGradient>
          <filter id="trfLedGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
          <filter id="trfHeatGlow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>

          <linearGradient id="radiatorHeatGrad" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor="#ea580c" stopOpacity="0.1" />
            <stop offset="50%" stopColor="#f97316" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#fde047" stopOpacity="0.95" />
          </linearGradient>
        </defs>

        {/* ── 1. Left & Right Radiator Convective Heat Arrows (Upward Convection) ── */}
        {isRunning && !isOffline && (
          <g filter="url(#trfHeatGlow)">
          {/* LEFT Radiator – 2 columns, 3 staggered phases. SVG-native animation (SVG units, not CSS px) */}
          {[
            { x: 26,  delays: [0,    0.7,  1.4 ] },
            { x: 44,  delays: [0.4,  1.1,  1.8 ] },
          ].map(({ x, delays }) =>
            delays.map((delay, i) => {
              const dur = heatDuration === "0s" ? "0s" : heatDuration;
              return (
                <g key={`lh-${x}-${i}`}>
                  <animateTransform
                    attributeName="transform"
                    type="translate"
                    from="0,0"
                    to="0,-110"
                    dur={dur}
                    begin={`${delay}s`}
                    repeatCount="indefinite"
                    calcMode="linear"
                    additive="sum"
                  />
                  <animate
                    attributeName="opacity"
                    values="0;0.9;0.85;0"
                    keyTimes="0;0.08;0.75;1"
                    dur={dur}
                    begin={`${delay}s`}
                    repeatCount="indefinite"
                  />
                  {/* Arrow: short line + arrowhead, all positioned within radiator fins area */}
                  <line x1={x} y1="275" x2={x} y2="248"
                    stroke="#ef4444" strokeWidth="2" strokeLinecap="round"
                  />
                  <polygon points={`${x-4},253 ${x},240 ${x+4},253`} fill="#ef4444" />
                </g>
              );
            })
          )}

          {/* RIGHT Radiator – 2 columns, 3 staggered phases */}
          {[
            { x: 447, delays: [0.2,  0.9,  1.6 ] },
            { x: 465, delays: [0.6,  1.3,  2.0 ] },
          ].map(({ x, delays }) =>
            delays.map((delay, i) => {
              const dur = heatDuration === "0s" ? "0s" : heatDuration;
              return (
                <g key={`rh-${x}-${i}`}>
                  <animateTransform
                    attributeName="transform"
                    type="translate"
                    from="0,0"
                    to="0,-110"
                    dur={dur}
                    begin={`${delay}s`}
                    repeatCount="indefinite"
                    calcMode="linear"
                    additive="sum"
                  />
                  <animate
                    attributeName="opacity"
                    values="0;0.9;0.85;0"
                    keyTimes="0;0.08;0.75;1"
                    dur={dur}
                    begin={`${delay}s`}
                    repeatCount="indefinite"
                  />
                  <line x1={x} y1="275" x2={x} y2="248"
                    stroke="#ef4444" strokeWidth="2" strokeLinecap="round"
                  />
                  <polygon points={`${x-4},253 ${x},240 ${x+4},253`} fill="#ef4444" />
                </g>
              );
            })
          )}
        </g>
        )}

        {/* ── 2. Top High-Voltage Bushings Electrical Arcs / Lightning (H1, H2, H3) ── */}
        {isRunning && !isOffline && (
          <g filter="url(#trfHeatGlow)">
            {/* Bushing H1 (x: 188, y: 70) */}
            <path
              d="M 188 35 L 188 120"
              stroke="#fbbf24"
              strokeWidth="2.5"
              strokeDasharray="8 4"
              strokeLinecap="round"
              style={{ animation: `scada-flow-forward ${fluxDuration} linear infinite` }}
            />
            <circle cx="188" cy="35" r="14" fill="url(#trfArcGlow)" style={{ animation: "scada-corona-arc 1.5s infinite" }} />

            {/* Bushing H2 (x: 256, y: 70) */}
            <path
              d="M 256 35 L 256 120"
              stroke="#fbbf24"
              strokeWidth="2.5"
              strokeDasharray="8 4"
              strokeLinecap="round"
              style={{ animation: `scada-flow-forward ${fluxDuration} linear infinite 0.2s` }}
            />
            <circle cx="256" cy="25" r="16" fill="url(#trfArcGlow)" style={{ animation: "scada-corona-arc 1.5s infinite 0.3s" }} />

            {/* Bushing H3 (x: 324, y: 70) */}
            <path
              d="M 324 35 L 324 120"
              stroke="#fbbf24"
              strokeWidth="2.5"
              strokeDasharray="8 4"
              strokeLinecap="round"
              style={{ animation: `scada-flow-forward ${fluxDuration} linear infinite 0.4s` }}
            />
            <circle cx="324" cy="35" r="14" fill="url(#trfArcGlow)" style={{ animation: "scada-corona-arc 1.5s infinite 0.6s" }} />
          </g>
        )}

        {/* ── 3. Internal Transformer Tank Magnetic Induction Flux Circulation ── */}
        {isRunning && !isOffline && (
          <g filter="url(#trfHeatGlow)" opacity="0.85">
            <rect
              x="138"
              y="170"
              width="215"
              height="150"
              rx="16"
              fill="none"
              stroke="#22c55e"
              strokeWidth="2.5"
              strokeDasharray="14 8"
              strokeLinecap="round"
              style={{ animation: `scada-flow-forward ${fluxDuration} linear infinite` }}
            />
          </g>
        )}

        {/* ── 4. Base Status LED Indicator (3 Distinct States) ── */}
        <circle
          cx="412"
          cy="348"
          r="6.5"
          fill={dotColor}
          stroke="#ffffff"
          strokeWidth="1.5"
          filter={isRunning || (!isOffline && statusColor === "red") ? "url(#trfLedGlow)" : "none"}
          style={{
            animation: isRunning ? "scada-led-blink 2s infinite ease-in-out" : "none",
          }}
        />
      </svg>
    </div>
  );
};

RealisticTransformer.propTypes = {
  liveValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
  isRunning: PropTypes.bool,
  isOffline: PropTypes.bool,
  statusColor: PropTypes.string,
  speedScale: PropTypes.number,
  opacity: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default RealisticTransformer;
